// Investigator Console data layer: auth, per-study aggregation, and exports.
// Reads are authorised by RLS (is_investigator()); participants stay anonymous.
import { supabase, isConfigured } from './supabase';

export { isConfigured };

// ---- auth ----
export async function getUser() {
  if (!isConfigured) return null;
  const { data } = await supabase.auth.getUser();
  return data?.user ?? null;
}
export async function signIn(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data.user;
}
export async function signUp(email, password) {
  const { data, error } = await supabase.auth.signUp({ email, password });
  if (error) throw error;
  return data;
}
export async function signOut() { if (isConfigured) await supabase.auth.signOut(); }

export async function isInvestigator() {
  if (!isConfigured) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || user.is_anonymous) return false;
  const { data, error } = await supabase.from('investigators').select('user_id').eq('user_id', user.id).maybeSingle();
  if (error) return false;
  return !!data;
}

// Returns the investigator's study scope: a study slug if scoped to one study,
// null if they're a lead (full access), or undefined if not an investigator.
// Pairs with migration 0009 (can_read_study + study_slug on investigators).
export async function investigatorStudy() {
  if (!isConfigured) return undefined;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || user.is_anonymous) return undefined;
  const { data, error } = await supabase.from('investigators').select('study_slug').eq('user_id', user.id).maybeSingle();
  if (error || !data) return undefined;
  return data.study_slug || null;
}

// Does the signed-in investigator still need to replace a default password?
export async function mustChangePassword() {
  if (!isConfigured) return false;
  const { data: { user } } = await supabase.auth.getUser();
  if (!user || user.is_anonymous) return false;
  const { data, error } = await supabase.from('investigators').select('must_change_password').eq('user_id', user.id).maybeSingle();
  if (error || !data) return false; // column missing (0010 not run) or no row → don't block
  return !!data.must_change_password;
}

// Set a new password for the signed-in user, then clear the must-change flag.
export async function changePassword(newPassword) {
  const { error } = await supabase.auth.updateUser({ password: newPassword });
  if (error) throw error;
  const { error: rerr } = await supabase.rpc('clear_password_change_flag');
  if (rerr) throw rerr;
}

// Readable temporary password, e.g. "Kap-Wqha-7438" (no ambiguous characters).
export function genPassword() {
  const U = 'ABCDEFGHJKLMNPQRSTUVWXYZ', L = 'abcdefghijkmnpqrstuvwxyz', D = '23456789';
  const pick = (s, n) => Array.from({ length: n }, () => s[Math.floor(Math.random() * s.length)]).join('');
  return `Kap-${pick(U, 1)}${pick(L, 3)}-${pick(D, 4)}`;
}

// Lead-only: provision (or re-scope) an investigator via the create-investigator
// Edge Function (service-role). Returns { ok, email, study_slug, existed }.
export async function createInvestigator({ email, study_slug, password, full_name }) {
  const { data, error } = await supabase.functions.invoke('create-investigator', {
    body: { email, study_slug: study_slug || null, password, full_name: full_name || null },
  });
  if (error) {
    let msg = error.message || 'Could not create investigator';
    try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch { /* no JSON body */ }
    throw new Error(msg);
  }
  return data;
}

// ---- data ----
// PostgREST caps a single select at ~1000 rows. The `response` table holds one row
// per item per phase, so a study with a few hundred participants easily exceeds that
// — a bare .select() would silently truncate the export. Page through with .range()
// (stable ordering so pages don't overlap or skip) and concatenate.
async function fetchAll(makeQuery) {
  const PAGE = 1000;
  const out = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await makeQuery().range(from, from + PAGE - 1);
    if (error) throw error;
    out.push(...(data || []));
    if (!data || data.length < PAGE) return out;
  }
}

export async function loadStudy(slug) {
  const [participants, responses, submissions, educationEvents] = await Promise.all([
    fetchAll(() => supabase.from('participant').select('*').eq('study_slug', slug).order('user_id')),
    fetchAll(() => supabase.from('response').select('user_id,phase,item_key,value').eq('study_slug', slug).order('user_id').order('phase').order('item_key')),
    fetchAll(() => supabase.from('submission').select('*').eq('study_slug', slug).order('user_id').order('phase')),
    fetchAll(() => supabase.from('education_event').select('user_id,module_id,time_spent_seconds,completed_at').eq('study_slug', slug).order('user_id')),
  ]);
  let feedback = [];
  try { feedback = await fetchAll(() => supabase.from('feedback').select('user_id,usefulness,learned').eq('study_slug', slug).order('user_id')); } catch { /* feedback table may not exist yet (migration 0007) */ }
  return { participants, responses, submissions, educationEvents, feedback };
}

// Lightweight per-study counts for the console landing page: distinct participants
// and distinct users who completed the final (post) phase, across all studies.
export async function loadStudyCounts() {
  if (!isConfigured) return {};
  const [pRows, sRows] = await Promise.all([
    fetchAll(() => supabase.from('participant').select('study_slug,user_id').order('user_id')),
    fetchAll(() => supabase.from('submission').select('study_slug,phase,user_id').order('user_id')),
  ]);
  const acc = {};
  const get = (slug) => (acc[slug] || (acc[slug] = { participants: new Set(), completedPost: new Set() }));
  for (const r of pRows) get(r.study_slug).participants.add(r.user_id);
  for (const r of sRows) if (r.phase === 'post') get(r.study_slug).completedPost.add(r.user_id);
  const out = {};
  for (const slug in acc) out[slug] = { participants: acc[slug].participants.size, completedPost: acc[slug].completedPost.size };
  return out;
}

// Perceived-benefit metric from the anonymous completion feedback (migration 0007).
export function computeFeedback(study, { feedback = [] }) {
  const useful = feedback.map((f) => f.usefulness).filter(isNum);
  const learnedYes = feedback.filter((f) => f.learned === 'yes').length;
  return {
    n: feedback.length,
    meanUsefulness: useful.length ? mean(useful) : null,
    learnedYesPct: feedback.length ? Math.round((learnedYes / feedback.length) * 100) : null,
  };
}

// Mailing-list sign-ups (separate from any study data). Throws if migration 0006
// hasn't been applied — the Console treats that as "not set up yet".
export async function loadSubscribers() {
  const { data, error } = await supabase.from('subscriber').select('*').order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

// ---- aggregation ----
// Scored dimensions and their submission columns. selfEfficacy uses a snake_case column
// (self_efficacy_score) so it can't be derived as `dim + '_score'` — map explicitly.
export const SCORE_DIMS = ['knowledge', 'attitude', 'selfEfficacy', 'practice'];
const SCORE_COL = { knowledge: 'knowledge_score', attitude: 'attitude_score', selfEfficacy: 'self_efficacy_score', practice: 'practice_score' };
const isNum = (v) => typeof v === 'number' && !Number.isNaN(v);
const mean = (a) => (a.length ? a.reduce((x, y) => x + y, 0) / a.length : null);
const stdev = (a) => { if (a.length < 2) return null; const m = mean(a); return Math.sqrt(a.reduce((s, x) => s + (x - m) ** 2, 0) / (a.length - 1)); };
const round = (v, d = 2) => (v == null || Number.isNaN(v) ? null : Math.round(v * 10 ** d) / 10 ** d);
function countBy(arr, fn) {
  const o = {};
  for (const x of arr) { const k = fn(x) ?? '—'; o[k] = (o[k] || 0) + 1; }
  return o;
}

export function computeOverview(study, { participants, responses, submissions }) {
  const phases = study.mode === 'pre-post' ? ['pre', 'post'] : ['cross'];
  const byUserPhase = {};
  for (const row of responses) {
    const k = row.user_id + '|' + row.phase;
    (byUserPhase[k] || (byUserPhase[k] = {}))[row.item_key] = row.value;
  }
  const completions = {};
  for (const ph of phases) completions[ph] = submissions.filter((s) => s.phase === ph).length;

  const scores = {};
  for (const ph of phases) {
    const subs = submissions.filter((s) => s.phase === ph);
    scores[ph] = {
      n: subs.length,
      knowledge: mean(subs.map((s) => s.knowledge_score).filter(isNum)),
      attitude: mean(subs.map((s) => s.attitude_score).filter(isNum)),
      selfEfficacy: mean(subs.map((s) => s.self_efficacy_score).filter(isNum)),
      practice: mean(subs.map((s) => s.practice_score).filter(isNum)),
    };
  }

  const items = [...(study.sections.knowledge || []), ...(study.sections.attitude || []), ...(study.sections.selfEfficacy || []), ...(study.sections.practice || [])];
  const distFor = (ph) => items.map((it) => {
    const counts = {};
    for (const k in byUserPhase) {
      if (!k.endsWith('|' + ph)) continue;
      const v = byUserPhase[k][it.key];
      if (v === undefined || v === null || v === '') continue;
      counts[v] = (counts[v] || 0) + 1;
    }
    return { key: it.key, stem: it.stem, type: it.type, counts };
  });
  const itemDistByPhase = {};
  for (const ph of phases) itemDistByPhase[ph] = distFor(ph);
  const itemDist = itemDistByPhase[phases[0]];

  return {
    participants: participants.length,
    completions,
    byCohort: countBy(participants, (p) => p.cohort),
    bySex: countBy(participants, (p) => p.sex),
    scores, itemDist, itemDistByPhase, phases,
  };
}

// Per-participant pre→post change for pre-post studies: deltas, mean, SD, 95% CI,
// and paired Cohen's d (dz) — the core inferential result for an intervention.
export function computePaired(study, { submissions }) {
  if (study.mode !== 'pre-post') return null;
  const byUser = {};
  for (const s of submissions) (byUser[s.user_id] || (byUser[s.user_id] = {}))[s.phase] = s;
  const pairs = [];
  for (const uid in byUser) { const u = byUser[uid]; if (u.pre && u.post) pairs.push({ uid, pre: u.pre, post: u.post }); }
  const dims = {};
  for (const dim of SCORE_DIMS) {
    const col = SCORE_COL[dim];
    const deltas = [], pres = [], posts = [], maxes = [];
    for (const p of pairs) {
      const a = p.pre[col], b = p.post[col];
      if (isNum(a) && isNum(b)) {
        deltas.push(b - a); pres.push(a); posts.push(b);
        // A submission's applicable practice denominator (NULL on legacy rows or for
        // students who marked items not-applicable) → fall back to the study's full max.
        if (dim === 'practice') maxes.push(isNum(p.pre.practice_max) ? p.pre.practice_max : (study.practiceMax || null));
      }
    }
    if (!deltas.length) { dims[dim] = null; continue; }
    const md = mean(deltas), sd = stdev(deltas);
    const se = sd != null ? sd / Math.sqrt(deltas.length) : null;
    dims[dim] = {
      n: deltas.length, preMean: mean(pres), postMean: mean(posts), meanDelta: md, sd,
      ci95: se != null ? [md - 1.96 * se, md + 1.96 * se] : null,
      cohensD: sd ? md / sd : null,
      ...(dim === 'practice' ? { max: mean(maxes.filter(isNum)) } : {}),
    };
  }
  return { n: pairs.length, pairs, dims };
}

// Data-quality signals an investigator should review/exclude before analysis.
export function computeQuality(study, { responses, submissions }) {
  const perUserPhase = {};
  for (const s of submissions) { const k = s.user_id + '|' + s.phase; perUserPhase[k] = (perUserPhase[k] || 0) + 1; }
  const duplicates = Object.values(perUserPhase).filter((c) => c > 1).length;
  const unscored = submissions.filter((s) => s.knowledge_score == null).length;
  const fast = submissions.filter((s) => isNum(s.duration_seconds) && s.duration_seconds > 0 && s.duration_seconds < 30).length;
  const att = {};
  for (const r of responses) { if (!/^AQ\d+$/.test(r.item_key)) continue; const k = r.user_id + '|' + r.phase; (att[k] || (att[k] = [])).push(Number(r.value)); }
  let straightLiners = 0;
  for (const k in att) { const vals = att[k].filter((v) => !Number.isNaN(v)); if (vals.length >= 4 && new Set(vals).size === 1) straightLiners++; }
  const scoredTimes = submissions.filter((s) => s.knowledge_score != null && s.submitted_at).map((s) => s.submitted_at).sort();
  return { duplicates, unscored, fast, straightLiners, lastScored: scoredTimes[scoredTimes.length - 1] || null };
}

// Recruitment + dropout funnel.
export function computeFunnel(study, { participants, responses, submissions, educationEvents = [] }) {
  const started = new Set([...participants.map((p) => p.user_id), ...responses.map((r) => r.user_id)]).size;
  const learned = new Set(educationEvents.map((e) => e.user_id)).size;
  const phases = study.mode === 'pre-post' ? ['pre', 'post'] : ['cross'];
  const completedPhase = {};
  for (const ph of phases) completedPhase[ph] = new Set(submissions.filter((s) => s.phase === ph).map((s) => s.user_id)).size;
  return { started, learned, completedPhase, phases };
}

// One row per (participant, phase): demographics + every item value + scores.
export function buildMaster(study, { participants, responses, submissions, educationEvents = [] }) {
  const pByUser = Object.fromEntries(participants.map((p) => [p.user_id, p]));
  const subBy = {};
  for (const s of submissions) subBy[s.user_id + '|' + s.phase] = s;
  const respBy = {};
  for (const r of responses) (respBy[r.user_id + '|' + r.phase] || (respBy[r.user_id + '|' + r.phase] = {}))[r.item_key] = r.value;
  const learnByUser = {};
  for (const ev of educationEvents) { const t = ev.time_spent_seconds; if (t == null) continue; learnByUser[ev.user_id] = Math.max(learnByUser[ev.user_id] || 0, t); }

  const extraDemo = (study.sections.demographics || []).map((i) => i.key).filter((k) => !['A1', 'A2', 'A3'].includes(k));
  const itemKeys = [
    ...(study.sections.knowledge || []),
    ...(study.sections.attitude || []),
    ...(study.sections.selfEfficacy || []),
    ...(study.sections.practice || []),
  ].map((i) => i.key);
  const columns = ['anon_id', 'phase', 'cohort', 'age', 'sex', ...extraDemo, ...itemKeys, 'knowledge_score', 'attitude_score', 'self_efficacy_score', 'practice_score', 'practice_max', 'learn_seconds', 'submitted_at'];

  const rows = [];
  for (const key of Object.keys(respBy)) {
    const [user_id, phase] = key.split('|');
    const p = pByUser[user_id] || {};
    const ans = respBy[key] || {};
    const sub = subBy[key] || {};
    const row = {
      anon_id: user_id.slice(0, 8), phase,
      cohort: p.cohort ?? '', age: p.age ?? '', sex: p.sex ?? '',
      knowledge_score: sub.knowledge_score ?? '', attitude_score: sub.attitude_score ?? '',
      self_efficacy_score: sub.self_efficacy_score ?? '',
      practice_score: sub.practice_score ?? '', practice_max: sub.practice_max ?? '',
      learn_seconds: phase === 'post' ? (learnByUser[user_id] ?? '') : '',
      submitted_at: sub.submitted_at ?? '',
    };
    for (const it of [...extraDemo, ...itemKeys]) row[it] = ans[it] ?? '';
    rows.push(row);
  }
  rows.sort((a, b) => (a.phase + a.anon_id).localeCompare(b.phase + b.anon_id));
  return { columns, rows };
}

// ---- exports ----
function stamp() { const d = new Date(); return d.toISOString().slice(0, 10); }

// Self-documenting data dictionary built from the study config (no answer keys —
// those stay server-side; reverse-coding is noted generically).
const LIKERT_LABELS = '1=Strongly disagree, 2=Disagree, 3=Neutral, 4=Agree, 5=Strongly agree';
function buildCodebook(study) {
  const rows = [['Item', 'Section', 'Type', 'Question / stem', 'Response values']];
  for (const sec of ['demographics', 'knowledge', 'attitude', 'selfEfficacy', 'practice']) {
    for (const it of (study.sections[sec] || [])) {
      let vals = '';
      if (it.type === 'likert') vals = LIKERT_LABELS + (sec === 'attitude' ? ' (negatively-worded items reverse-coded server-side)' : '');
      else if (it.options) vals = it.options.map((o) => `${o.value}=${o.label}`).join('; ');
      else if (it.type === 'number') vals = `number${it.min != null ? ` (${it.min}–${it.max})` : ''}`;
      rows.push([it.key, sec, it.type, it.stem, vals]);
    }
  }
  return rows;
}

// Builds a per-study workbook: Master Data + Summary (with paired pre→post stats) +
// Codebook + Paired changes + Data quality + Participant flow.
function buildWorkbook(XLSX, study, data) {
  const wb = XLSX.utils.book_new();
  const { columns, rows } = buildMaster(study, data);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows, { header: columns }), 'Master Data');

  const ov = computeOverview(study, data);
  const paired = computePaired(study, data);
  const learnTimes = (data.educationEvents || []).map((e) => e.time_spent_seconds).filter((t) => typeof t === 'number');
  const avgLearn = learnTimes.length ? Math.round(learnTimes.reduce((a, b) => a + b, 0) / learnTimes.length) : null;
  const summary = [
    ['KAPSTAT export'], ['Study', study.studyTitle], ['Slug', study.slug], ['Mode', study.mode], ['Instrument version', study.version || '—'],
    ['Exported', new Date().toISOString()], [], ['Participants', ov.participants],
    ...Object.entries(ov.completions).map(([ph, n]) => ['Completions (' + ph + ')', n]),
    ['Learning-module reads', learnTimes.length],
    ['Avg learning time (s)', avgLearn ?? '—'],
  ];
  if (paired) {
    summary.push([], ['Paired pre→post change (n=' + paired.n + ' with both phases)']);
    for (const dim of SCORE_DIMS) {
      const d = paired.dims[dim];
      summary.push(d
        ? [dim, `${round(d.preMean)} → ${round(d.postMean)} | Δ ${round(d.meanDelta)} (SD ${round(d.sd)}), Cohen's d ${round(d.cohensD)}`]
        : [dim, 'not scored']);
    }
  }
  const fb = computeFeedback(study, data);
  if (fb.n) {
    summary.push([], ['Perceived benefit (anonymous completion feedback, n=' + fb.n + ')'],
      ['Mean usefulness (1-5)', round(fb.meanUsefulness)],
      ['Learned something new (%)', fb.learnedYesPct]);
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');

  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(buildCodebook(study)), 'Codebook');

  if (paired) {
    const pcols = ['anon_id', ...SCORE_DIMS.flatMap((d) => [d + '_pre', d + '_post', d + '_delta'])];
    const dlt = (a, b) => (isNum(a) && isNum(b) ? b - a : '');
    const prows = paired.pairs.map((p) => {
      const row = { anon_id: p.uid.slice(0, 8) };
      for (const dim of SCORE_DIMS) {
        const a = p.pre[SCORE_COL[dim]], b = p.post[SCORE_COL[dim]];
        row[dim + '_pre'] = a ?? ''; row[dim + '_post'] = b ?? ''; row[dim + '_delta'] = dlt(a, b);
      }
      return row;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(prows, { header: pcols }), 'Paired changes');
  }

  const q = computeQuality(study, data);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
    ['Data-quality flags — review/exclude before analysis'], [],
    ['Duplicate submissions (same user & phase)', q.duplicates],
    ['Unscored final submissions (score fn was down)', q.unscored],
    ['Very fast submissions (<30s)', q.fast],
    ['Possible straight-lining (zero attitude variance)', q.straightLiners],
    ['Last scored at', q.lastScored || '—'],
  ]), 'Data quality');

  const f = computeFunnel(study, data);
  const flow = [['Participant flow (recruitment & dropout)'], [], ['Started (consented / any response)', f.started], ['Completed learning module', f.learned]];
  for (const ph of f.phases) flow.push(['Completed ' + ph, f.completedPhase[ph]]);
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(flow), 'Participant flow');

  return wb;
}

export async function exportXlsx(study, data) {
  const XLSX = await import('xlsx');
  XLSX.writeFile(buildWorkbook(XLSX, study, data), `kapstat_${study.slug}_${stamp()}.xlsx`);
}

// Downloads one .xlsx per study (each filtered to its own study_slug). Loads each
// study's data on demand and staggers the saves so the browser allows the batch.
export async function exportAllXlsx(studyList) {
  const XLSX = await import('xlsx');
  for (const study of studyList) {
    let data;
    try { data = await loadStudy(study.slug); } catch { continue; }
    XLSX.writeFile(buildWorkbook(XLSX, study, data), `kapstat_${study.slug}_${stamp()}.xlsx`);
    await new Promise((r) => setTimeout(r, 500));
  }
}

export function exportCsv(study, data) {
  const { columns, rows } = buildMaster(study, data);
  const esc = (v) => { v = v == null ? '' : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
  const csv = [columns.join(','), ...rows.map((r) => columns.map((c) => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `kapstat_${study.slug}_${stamp()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export function exportSubscribersCsv(rows) {
  const cols = ['email', 'role', 'field', 'study_year', 'experience', 'source', 'created_at'];
  const esc = (v) => { v = v == null ? '' : String(v); return /[",\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v; };
  const csv = [cols.join(','), ...rows.map((r) => cols.map((c) => esc(r[c])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `kapstat_subscribers_${stamp()}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}
